import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  Users, Plus, Edit, Trash2, Eye, Shield, Building2, Search,
  Loader2, X, UserCog, ChevronDown
} from 'lucide-react';

export default function AdminUsersPage() {
  const { user: currentUser, isAdmin, isCompanyAdmin, impersonate } = useAuth();
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', password: '',
    employee_number: '', phone_number: '', role: 'USER', company_id: '', is_active: true,
  });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const u = await api.get('/api/users');
      setUsers(u);
      if (isAdmin) {
        const c = await api.get('/api/companies');
        setCompanies(c);
      }
    } catch (err) { toast.error(err.message); }
    setLoading(false);
  };

  const openCreate = () => {
    setEditing(null);
    setForm({
      first_name: '', last_name: '', email: '', password: '',
      employee_number: '', phone_number: '', role: 'USER',
      company_id: isCompanyAdmin ? currentUser.company_id : '', is_active: true,
    });
    setShowModal(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({
      first_name: u.first_name, last_name: u.last_name, email: u.email, password: '',
      employee_number: u.employee_number, phone_number: u.phone_number || '',
      role: u.role, company_id: u.company_id || '', is_active: u.is_active,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      if (editing) {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        await api.put(`/api/users/${editing.id}`, payload);
        toast.success('Usuario actualizado');
      } else {
        if (!form.password || form.password.length < 6) {
          toast.error('La contraseña debe tener al menos 6 caracteres');
          return;
        }
        await api.post('/api/users', form);
        toast.success('Usuario creado');
      }
      setShowModal(false);
      loadData();
    } catch (err) { toast.error(err.message); }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`¿Eliminar a ${u.first_name} ${u.last_name}?`)) return;
    try {
      await api.del(`/api/users/${u.id}`);
      toast.success('Usuario eliminado');
      loadData();
    } catch (err) { toast.error(err.message); }
  };

  const handleImpersonate = async (u) => {
    try {
      await impersonate(u.id);
      toast.success(`Impersonando a ${u.first_name}`);
    } catch (err) { toast.error(err.message); }
  };

  const roleLabel = { ADMIN: 'Admin', COMPANY_ADMIN: 'Admin Empresa', USER: 'Usuario' };
  const roleColor = { ADMIN: '#ef4444', COMPANY_ADMIN: '#f59e0b', USER: '#6366f1' };

  const filtered = users.filter(u =>
    !search || `${u.first_name} ${u.last_name} ${u.email}`.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <div className="flex items-center justify-center h-full"><Loader2 size={28} className="animate-spin" style={{ color: 'var(--accent-primary)' }} /></div>;

  return (
    <div className="p-6 lg:p-8 max-w-6xl" data-testid="admin-users-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>Gestion de Usuarios</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>{filtered.length} usuarios</p>
        </div>
        <button onClick={openCreate} data-testid="create-user-btn" className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-white" style={{ backgroundColor: 'var(--accent-primary)' }}>
          <Plus size={16} /> Nuevo Usuario
        </button>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
        <input
          type="text" value={search} onChange={e => setSearch(e.target.value)}
          data-testid="search-users"
          placeholder="Buscar usuarios..."
          className="w-full pl-10 pr-4 py-2.5 rounded-lg border text-sm outline-none"
          style={{ backgroundColor: 'var(--surface-primary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
        />
      </div>

      {/* Table */}
      <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: 'var(--surface-primary)', borderColor: 'var(--border-color)' }}>
        <table className="w-full text-sm">
          <thead>
            <tr style={{ backgroundColor: 'var(--surface-secondary)' }}>
              <th className="text-left px-4 py-3 font-medium text-xs" style={{ color: 'var(--text-muted)' }}>Nombre</th>
              <th className="text-left px-4 py-3 font-medium text-xs" style={{ color: 'var(--text-muted)' }}>Email</th>
              <th className="text-left px-4 py-3 font-medium text-xs" style={{ color: 'var(--text-muted)' }}>Rol</th>
              <th className="text-left px-4 py-3 font-medium text-xs" style={{ color: 'var(--text-muted)' }}>Empresa</th>
              <th className="text-left px-4 py-3 font-medium text-xs" style={{ color: 'var(--text-muted)' }}>Estado</th>
              <th className="text-right px-4 py-3 font-medium text-xs" style={{ color: 'var(--text-muted)' }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(u => (
              <tr key={u.id} className="border-t transition-colors" style={{ borderColor: 'var(--border-color)' }}>
                <td className="px-4 py-3">
                  <span className="font-medium" style={{ color: 'var(--text-primary)' }}>{u.first_name} {u.last_name}</span>
                  <br /><span className="text-[10px] font-mono" style={{ color: 'var(--text-muted)' }}>{u.employee_number}</span>
                </td>
                <td className="px-4 py-3" style={{ color: 'var(--text-secondary)' }}>{u.email}</td>
                <td className="px-4 py-3">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ backgroundColor: `${roleColor[u.role]}20`, color: roleColor[u.role] }}>
                    <Shield size={9} /> {roleLabel[u.role] || u.role}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs" style={{ color: 'var(--text-muted)' }}>{u.company_name || '-'}</td>
                <td className="px-4 py-3">
                  <span className={`inline-block w-2 h-2 rounded-full ${u.is_active ? 'bg-green-500' : 'bg-red-500'}`} />
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-1">
                    {u.id !== currentUser.id && u.role !== 'ADMIN' && (
                      <button onClick={() => handleImpersonate(u)} title="Impersonar" data-testid={`impersonate-${u.id}`}
                        className="p-1.5 rounded-md transition-colors hover:bg-white/10" style={{ color: 'var(--text-muted)' }}>
                        <Eye size={14} />
                      </button>
                    )}
                    <button onClick={() => openEdit(u)} title="Editar" data-testid={`edit-user-${u.id}`}
                      className="p-1.5 rounded-md transition-colors hover:bg-white/10" style={{ color: 'var(--text-muted)' }}>
                      <Edit size={14} />
                    </button>
                    {u.id !== currentUser.id && (
                      <button onClick={() => handleDelete(u)} title="Eliminar" data-testid={`delete-user-${u.id}`}
                        className="p-1.5 rounded-md transition-colors hover:bg-red-500/10" style={{ color: 'var(--text-muted)' }}>
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="text-center py-10" style={{ color: 'var(--text-muted)' }}>
            <Users size={28} className="mx-auto mb-2 opacity-40" />
            <p className="text-sm">No se encontraron usuarios</p>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg rounded-xl border p-6 max-h-[90vh] overflow-y-auto" style={{ backgroundColor: 'var(--surface-primary)', borderColor: 'var(--border-color)' }} data-testid="user-modal">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-heading font-bold text-lg" style={{ color: 'var(--text-primary)' }}>
                {editing ? 'Editar Usuario' : 'Nuevo Usuario'}
              </h3>
              <button onClick={() => setShowModal(false)} style={{ color: 'var(--text-muted)' }}><X size={20} /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Nombre *" value={form.first_name} onChange={v => setForm(p => ({ ...p, first_name: v }))} testId="field-first-name" />
              <Field label="Apellidos *" value={form.last_name} onChange={v => setForm(p => ({ ...p, last_name: v }))} testId="field-last-name" />
              <Field label="Email *" value={form.email} onChange={v => setForm(p => ({ ...p, email: v }))} type="email" testId="field-email" />
              <Field label={editing ? 'Nueva contraseña' : 'Contraseña *'} value={form.password} onChange={v => setForm(p => ({ ...p, password: v }))} type="password" testId="field-password" />
              <Field label="N. Empleado *" value={form.employee_number} onChange={v => setForm(p => ({ ...p, employee_number: v }))} testId="field-employee" />
              <Field label="Telefono" value={form.phone_number} onChange={v => setForm(p => ({ ...p, phone_number: v }))} testId="field-phone" />
              {isAdmin && (
                <div>
                  <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Rol</label>
                  <select value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value }))} data-testid="field-role"
                    className="w-full px-3 py-2 rounded-lg border text-sm outline-none" style={{ backgroundColor: 'var(--surface-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
                    <option value="USER">Usuario</option>
                    <option value="COMPANY_ADMIN">Admin Empresa</option>
                    <option value="ADMIN">Administrador</option>
                  </select>
                </div>
              )}
              {isAdmin && (
                <div>
                  <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Empresa</label>
                  <select value={form.company_id} onChange={e => setForm(p => ({ ...p, company_id: e.target.value }))} data-testid="field-company"
                    className="w-full px-3 py-2 rounded-lg border text-sm outline-none" style={{ backgroundColor: 'var(--surface-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
                    <option value="">Sin empresa</option>
                    {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              )}
              <div className="col-span-2 flex items-center gap-2 mt-1">
                <input type="checkbox" checked={form.is_active} onChange={e => setForm(p => ({ ...p, is_active: e.target.checked }))} data-testid="field-active" />
                <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Activo</span>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 rounded-lg text-sm font-medium" style={{ color: 'var(--text-muted)' }}>Cancelar</button>
              <button onClick={handleSave} data-testid="save-user-btn" className="px-5 py-2 rounded-lg text-sm font-semibold text-white" style={{ backgroundColor: 'var(--accent-primary)' }}>
                {editing ? 'Guardar' : 'Crear'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', testId }) {
  return (
    <div>
      <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} data-testid={testId}
        className="w-full px-3 py-2 rounded-lg border text-sm outline-none"
        style={{ backgroundColor: 'var(--surface-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
    </div>
  );
}
